import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FiMail, FiMapPin, FiPhone, FiSend, FiCheckCircle } from 'react-icons/fi';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    category: '',
    budget: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('/api/send-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to send request');
      }

      setSubmitSuccess(true);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        company: '',
        category: '',
        budget: '',
        message: '',
      });

      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      console.error('Contact form submit error:', err);
      alert('Failed to send message. Please try again later or email info@neovam.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: FiMapPin,
      title: 'Address',
      content: ['P.O BOX 36098', 'Kigamboni, Dar es Salaam', 'Tanzania'],
      color: 'from-blue-400 to-blue-600',
    },
    {
      icon: FiMail,
      title: 'Email',
      content: ['info@neovam.com', 'sales@neovam.com'],
      color: 'from-green-400 to-green-600',
      link: 'mailto:info@neovam.com',
    },
    {
      icon: FiPhone,
      title: 'Phone',
      content: ['+255 746 177 230', 'Mon-Fri: 8AM - 5PM'],
      color: 'from-purple-400 to-purple-600',
      link: 'tel:+255000000000',
    },
  ];

  const faqs = [
    {
      question: 'How quickly can you source products?',
      answer: 'Most standard items are available within 24-48 hours. Custom or specialized items may take 3-5 business days depending on supplier availability.',
    },
    {
      question: 'What are your payment terms?',
      answer: 'We offer flexible payment options including cash on delivery, bank transfer, mobile money, and credit accounts for verified businesses with NET 30 or NET 60 terms.',
    },
    {
      question: 'Do you deliver outside Dar es Salaam?',
      answer: 'Yes, we deliver nationwide across Tanzania. We have competitive shipping rates and partner with reliable logistics companies for regional deliveries.',
    },
    {
      question: 'Can you handle bulk or project-based orders?',
      answer: 'Absolutely! We specialize in large-scale procurement for projects, institutions, and organizations. Our team can handle orders of any size.',
    },
    {
      question: 'Do you provide quotes before ordering?',
      answer: 'Yes, all clients receive detailed quotations with itemized pricing before any purchase. We believe in complete transparency with no hidden costs.',
    },
    {
      question: 'What is your minimum order value?',
      answer: 'We have no minimum order value. Whether you need a single item or bulk supplies, we are here to serve you with the same level of professionalism.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us - NeoVam General Supplies</title>
        <meta name="description" content="Get in touch with NeoVam General Supplies. Request a quote, ask questions, or partner with us for your business supply needs." />
      </Helmet>

      <div className="min-h-screen pt-[120px]">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-dark-950 dark:via-dark-900 dark:to-dark-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 animate-fade-in">
              Get in <span className="text-gradient">Touch</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto animate-slide-up">
              Ready to simplify your procurement? We're here to help with any questions or supply requests.
            </p>
          </div>
        </section>

        {/* Contact Cards */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {contactCards.map((card, index) => (
                <div
                  key={index}
                  className="card text-center hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mx-auto mb-4`}>
                    <card.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {card.title}
                  </h3>
                  <div className="space-y-1">
                    {card.content.map((line, lineIndex) => (
                      <p key={lineIndex} className="text-gray-600 dark:text-gray-400">
                        {card.link && lineIndex === 0 ? (
                          <a href={card.link} className="hover:text-primary-400 transition-colors duration-300">
                            {line}
                          </a>
                        ) : (
                          line
                        )}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Map */}
        <section className="py-12 sm:py-16 md:py-20 bg-gray-50 dark:bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="animate-slide-in-left">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Send Us a <span className="text-gradient">Message</span>
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                {submitSuccess && (
                  <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-400 rounded-xl flex items-center gap-3 animate-fade-in">
                    <FiCheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    <p className="text-green-800 dark:text-green-300 font-semibold">
                      Message sent successfully! We'll be in touch soon.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        placeholder="+255 746 177 230"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Company/Organization *
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        placeholder="Company Name"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Product Category of Interest
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                      >
                        <option value="">Select a category</option>
                        <option value="office">Office Stationery & Supplies</option>
                        <option value="ict">ICT Equipment & Accessories</option>
                        <option value="cleaning">Cleaning & Janitorial Supplies</option>
                        <option value="construction">Building & Construction Materials</option>
                        <option value="hospitality">Hospitality Supplies</option>
                        <option value="custom">Custom Procurement</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Budget Range
                      </label>
                      <select
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                      >
                        <option value="">Select budget range</option>
                        <option value="under-1m">Under 1M TZS</option>
                        <option value="1m-5m">1M - 5M TZS</option>
                        <option value="5m-10m">5M - 10M TZS</option>
                        <option value="10m-50m">10M - 50M TZS</option>
                        <option value="over-50m">Over 50M TZS</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                      Message / Requirements *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="6"
                      className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-dark-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell us about your supply needs..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-primary text-white text-lg font-semibold rounded-xl hover:shadow-glow hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Request'}
                    <FiSend />
                  </button>
                </form>
              </div>

              {/* Map Placeholder */}
              <div className="animate-slide-in-right">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                  Find <span className="text-gradient">Our Location</span>
                </h2>
                <div className="card h-[500px] p-0 overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126646.31060192148!2d39.26051!3d-6.82349!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x185c4b7f8b0f3b8d%3A0x7f7f7f7f7f7f7f7f!2sKigamboni%2C%20Dar%20es%20Salaam%2C%20Tanzania!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="NeoVam General Supplies Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Quick answers to common procurement questions
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="card animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 flex items-start gap-3">
                    <FiCheckCircle className="w-6 h-6 text-primary-400 flex-shrink-0 mt-1" />
                    {faq.question}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed pl-9">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
